import { useEffect, useState } from "react";
import { Clock, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { getOpenStatus } from "@/lib/hours";
import { OrderButton } from "@/components/TrackedLinks";

const THRESHOLD_MIN = 30;
const KEY = "tijuas-closing-dismissed";

// Dev/QA hook: append ?closing=12 to force the reminder with N minutes left.
const forcedMinutes = () => {
  const v = new URLSearchParams(window.location.search).get("closing");
  return v ? Number(v) : null;
};

const useMinutesToClose = (lang) => {
  const compute = () => forcedMinutes() ?? (getOpenStatus(lang).isOpen ? getOpenStatus(lang).minutesToClose : null);
  const [minutes, setMinutes] = useState(compute);
  useEffect(() => {
    setMinutes(compute());
    const id = window.setInterval(() => setMinutes(compute()), 60_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  return minutes;
};

export const ClosingReminder = () => {
  const { t, lang } = useLang();
  const minutes = useMinutesToClose(lang);
  const [dismissed, setDismissed] = useState(() => window.sessionStorage.getItem(KEY) === "1");

  if (dismissed || minutes == null || minutes > THRESHOLD_MIN || minutes <= 0) return null;

  const dismiss = () => { window.sessionStorage.setItem(KEY, "1"); setDismissed(true); };
  const closesAt = getOpenStatus(lang).closesAt;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed z-40 left-3 right-3 bottom-[4.5rem] md:left-auto md:right-6 md:bottom-6 md:w-[380px] bg-hacienda-charcoal text-hacienda-cream rounded-2xl shadow-2xl border border-white/10 p-4 animate-fade-in-up"
      data-testid="closing-reminder"
    >
      <div className="flex items-start gap-3">
        <div className="bg-hacienda-amber/20 p-2 rounded-lg flex-shrink-0"><Clock size={20} className="text-hacienda-amber" aria-hidden="true" /></div>
        <div className="flex-grow min-w-0">
          <p className="font-semibold leading-snug" data-testid="closing-reminder-title">
            {t("closing.title").replace("{min}", String(minutes))}
          </p>
          <p className="text-sm text-hacienda-cream/75 mt-1">{t("closing.copy").replace("{time}", closesAt || "")}</p>
          <OrderButton location="closing_reminder" size="sm" className="mt-3" data-testid="closing-reminder-order-btn" />
        </div>
        <button onClick={dismiss} aria-label={t("actions.close")} className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0" data-testid="closing-reminder-close">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
